import React, { useState, useEffect, useRef } from 'react';
import { Zap, Play, Square, CheckCircle2, AlertCircle, History, Settings2, Timer, Save, TrendingUp, RefreshCw, Search } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { doc, onSnapshot, updateDoc, setDoc, getDoc, getDocs, serverTimestamp, collection, query, where, increment, orderBy, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { formatDisplayName, maskString } from '@/shared/utils/formatters';

const AdminDayTradingPage: React.FC = () => {
    const [isRunning, setIsRunning] = useState(true);
    const [systemData, setSystemData] = useState<any>(null);
    const [totalLong, setTotalLong] = useState(0);
    const [totalShort, setTotalShort] = useState(0);
    const [currentRoundBets, setCurrentRoundBets] = useState<any[]>([]);
    const [recentResults, setRecentResults] = useState<any[]>([]);
    const [editingBetId, setEditingBetId] = useState<string | null>(null);
    const [editAmount, setEditAmount] = useState<string>('');
    const [isSaving, setIsSaving] = useState(false);

    // Settled transactions state
    const [settledTransactions, setSettledTransactions] = useState<any[]>([]);
    const [editingSettledId, setEditingSettledId] = useState<string | null>(null);
    const [editSettledAmount, setEditSettledAmount] = useState<string>('');

    // Toast state
    const [toastMessage, setToastMessage] = useState<string>('');
    const [showToast, setShowToast] = useState(false);

    const totalLongRef = React.useRef(0);
    const totalShortRef = React.useRef(0);
    const isFlippingRef = React.useRef(false);
    const lastSettledRoundRef = useRef<number>(0);
    const isSettlingRef = useRef(false);

    useEffect(() => { totalLongRef.current = totalLong; }, [totalLong]);
    useEffect(() => { totalShortRef.current = totalShort; }, [totalShort]);

    // Settings state
    const [settings, setSettings] = useState({
        winningMultiplier: '1.95',
        roundDuration: '120',
        maxBetAmount: '10000000'
    });

    const [localTimeLeft, setLocalTimeLeft] = useState(120);
    const [binancePredictions, setBinancePredictions] = useState<Record<number, 'UP' | 'DOWN'>>({});
    const [predictionsLoading, setPredictionsLoading] = useState(false);

    // Calculate current round based on time of day (2-minute intervals, 720 rounds/day)
    const calculateCurrentRound = () => {
        const now = new Date();
        const totalMinutes = (now.getHours() * 60) + now.getMinutes();
        return Math.floor(totalMinutes / 2) + 1;
    };

    const getRoundId = (date: Date, roundNumber?: number) => {
        const targetDate = new Date(date);
        if (roundNumber !== undefined) {
            const currentR = calculateCurrentRound();
            if (roundNumber > 600 && currentR < 100) {
                targetDate.setDate(targetDate.getDate() - 1);
            } else if (roundNumber < 100 && currentR > 600) {
                targetDate.setDate(targetDate.getDate() + 1);
            }
        }
        const yyyy = targetDate.getFullYear();
        const mm = String(targetDate.getMonth() + 1).padStart(2, '0');
        const dd = String(targetDate.getDate()).padStart(2, '0');
        const rNum = roundNumber || calculateCurrentRound();
        return `${yyyy}-${mm}-${dd}-${rNum}`;
    };

    // Calculate next 2-minute boundary
    const getNextMinuteBoundary = () => {
        const now = new Date();
        const next = new Date(now);
        next.setSeconds(0);
        next.setMilliseconds(0);
        const currentMins = next.getMinutes();
        const remainder = currentMins % 2;
        next.setMinutes(currentMins + (2 - remainder));
        return next;
    };


    // Sync with Firestore
    useEffect(() => {
        const docRef = doc(db, 'system', 'day-trading');

        // Initialize doc if not exists
        const initDoc = async () => {
            const snap = await getDoc(docRef);
            if (!snap.exists()) {
                const targetRound = calculateCurrentRound();
                const targetRoundId = getRoundId(new Date(), targetRound);
                const targetEndTime = getNextMinuteBoundary();

                await setDoc(docRef, {
                    currentRound: targetRound,
                    currentRoundId: targetRoundId,
                    endTime: targetEndTime,
                    status: 'ACTIVE',
                    winningMultiplier: '1.95',
                    roundDuration: '120',
                    maxBetAmount: '10000000',
                    resultOverride: 'AUTO',
                    futureResults: {},
                    lastUpdated: serverTimestamp()
                });
            }
        };
        initDoc();

        const unsubscribe = onSnapshot(docRef, async (snap) => {
            if (snap.exists()) {
                const data = snap.data();

                // Auto-Correction for Round & Time desync
                const targetRound = calculateCurrentRound();
                const now = new Date().getTime();
                const endPos = data.endTime ? (typeof data.endTime.toDate === 'function' ? data.endTime.toDate().getTime() : new Date(data.endTime).getTime()) : 0;

                // If round is off by more than 1 (allowing generic transition) or time is stuck in past
                // We use targetRound. If DB says 1482 and target is 700, force update.
                if ((data.status === 'ACTIVE') && (data.currentRound !== targetRound || endPos <= now - 5000)) { // 5s buffer
                    if (!isFlippingRef.current) {
                        // FIX: Settle ALL missed rounds between currentRound and targetRound
                        const targetEndTime = getNextMinuteBoundary();
                        const startRound = data.currentRound;
                        const endRound = targetRound;

                        console.log(`[Sync] Handling transition from ${startRound} to ${endRound}`);

                        for (let r = startRound; r < endRound; r++) {
                            if (r > 0 && r !== lastSettledRoundRef.current) {
                                console.log(`[Sync] Catching up on missed round #${r}`);
                                // Use a special flag or just ensure sequential await works
                                await autoSettleRound(r, data.winningMultiplier || '1.95', undefined, true);
                            }
                        }

                        // 3. Mark currentRound as progressed
                        const targetRoundId = getRoundId(new Date(), targetRound);
                        await updateDoc(doc(db, 'system', 'day-trading'), {
                            currentRound: targetRound,
                            currentRoundId: targetRoundId,
                            endTime: targetEndTime,
                            lastUpdated: serverTimestamp()
                        });
                        // Don't return early here, let it set systemData below
                    }
                } else {
                    // Even if no auto-correction, sync local state
                    setSystemData(data);
                    setIsRunning(data.status === 'ACTIVE');
                    setSettings({
                        winningMultiplier: data.winningMultiplier || '1.95',
                        roundDuration: data.roundDuration || '120',
                        maxBetAmount: data.maxBetAmount || '10000000'
                    });
                }
            }
        });

        return () => unsubscribe();
    }, []);

    // Timer for local countdown
    useEffect(() => {
        const interval = setInterval(() => {
            if (systemData?.endTime) {
                const now = new Date().getTime();
                const endPos = systemData.endTime.toDate ? systemData.endTime.toDate().getTime() : new Date(systemData.endTime).getTime();
                const diff = Math.floor((endPos - now) / 1000);
                setLocalTimeLeft(diff > 0 ? diff : 0);
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [systemData?.endTime]);

    // Listen to recent results
    useEffect(() => {
        const currentRound = Number(systemData?.currentRound || 0);
        if (currentRound === 0) return;

        // Robust query: only fetch rounds LOWER than current to bypass garbage/experimental data
        const q = query(
            collection(db, 'round_results'),
            where('round', '<', currentRound),
            orderBy('round', 'desc'),
            limit(5)
        );

        const unsubscribe = onSnapshot(q, (snap) => {
            const results = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Snapshot already ordered desc, but ensure sorting for UI
            results.sort((a: any, b: any) => (b.round || 0) - (a.round || 0));
            setRecentResults(results);
        });
        return () => unsubscribe();
    }, [systemData?.currentRound]);

    // Listen to real-time betting volumes and individual bets
    useEffect(() => {
        if (!systemData?.currentRound) return;

        const q = query(
            collection(db, 'transactions'),
            where('type', 'in', ['TRADING_BUY', 'TRADING_SELL'])
        );

        const unsubscribe = onSnapshot(q, (snap) => {
            let long = 0;
            let short = 0;
            const bets: any[] = [];
            const currentRound = systemData.currentRound;

            snap.docs.forEach(doc => {
                const data = doc.data();
                // round 타입 통일 + 이미 정산된 베팅은 제외 + 최근 1시간 데이터만 표시 (유령 베팅 방지)
                const docRound = Number(data.metadata?.round);
                const isRecent = data.timestamp?.seconds ? (data.timestamp.seconds > (Date.now() / 1000) - 3600) : true;

                if (docRound === Number(currentRound) && data.settled !== true && isRecent) {
                    const amount = typeof data.amount === 'string'
                        ? parseInt(data.amount.replace(/,/g, ''))
                        : data.amount;

                    const direction = data.metadata?.direction || (data.type === 'TRADING_BUY' ? 'UP' : 'DOWN');

                    if (direction === 'UP') long += amount;
                    else if (direction === 'DOWN') short += amount;

                    bets.push({
                        id: doc.id,
                        userId: data.userId,
                        userName: data.userName || 'Anonymous',
                        amount,
                        direction,
                        timestamp: data.timestamp
                    });
                }
            });

            setTotalLong(long);
            setTotalShort(short);
            setCurrentRoundBets(bets.sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0)));
        });

        return () => unsubscribe();
    }, [systemData?.currentRound]);

    // Listen to settled transactions
    useEffect(() => {
        const q = query(
            collection(db, 'transactions'),
            where('type', 'in', ['TRADING_BUY', 'TRADING_SELL'])
        );

        const unsubscribe = onSnapshot(q, (snap) => {
            const allTrades = snap.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    timestamp: doc.data().timestamp || doc.data().createdAt
                }))
                // WIN 수익 지급 트랜잭션(메타 type:WIN)은 제외
                .filter((tx: any) => tx.metadata?.type !== 'WIN');

            // Sort by timestamp descending and take recent 30
            allTrades.sort((a: any, b: any) => {
                const aTime = a.timestamp?.seconds || 0;
                const bTime = b.timestamp?.seconds || 0;
                return bTime - aTime;
            });

            setSettledTransactions(allTrades.slice(0, 30));
        });

        return () => unsubscribe();
    }, []);

    // ✅ AUTO TRIGGER: Settle the PREVIOUS round when currentRound changes
    const prevRoundRef = useRef<number>(0);
    useEffect(() => {
        const currentRound = systemData?.currentRound;
        if (!currentRound || currentRound <= 1) return;

        const prevRound = currentRound - 1;

        // Only trigger if round actually changed (not initial mount)
        if (prevRoundRef.current !== 0 && prevRoundRef.current === prevRound) {
            // Already triggered for this prev round
        } else if (prevRoundRef.current !== currentRound) {
            prevRoundRef.current = currentRound;
            // Settle previous round automatically
            console.log(`[AdminAutoSettle] Round changed to #${currentRound}, settling previous round #${prevRound}`);
            setTimeout(() => {
                autoSettleRound(prevRound, settings.winningMultiplier || '1.95');
            }, 3000); // 3s delay to ensure round transition is complete
        }
    }, [systemData?.currentRound]);


    // Toast auto-hide effect
    useEffect(() => {
        if (showToast) {
            const timer = setTimeout(() => {
                setShowToast(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [showToast]);

    const showToastMessage = (message: string) => {
        setToastMessage(message);
        setShowToast(true);
    };

    // ===== AUTO-SETTLEMENT LOGIC =====
    // Automatically settle all unsettled transactions for a completed round
    // NOTE: This is a CLIENT-SIDE fallback. The primary settlement happens server-side via:
    //   1. manageDayTradingRound (cron every 1 min)
    //   2. requestInstantSettle (user-triggered HTTP)
    //   3. onRoundResultCreated (Firestore trigger on round_results)
    const autoSettleRound = async (roundNum: number, multiplierStr: string, overrideResult?: 'UP' | 'DOWN', skipLock: boolean = false) => {
        if (!skipLock && isSettlingRef.current) return;
        if (lastSettledRoundRef.current >= roundNum && !skipLock) return;
        isSettlingRef.current = true;

        try {
            console.log(`[AutoSettle] Starting settlement for round #${roundNum}`);

            // 1. Use explicitly passed override first
            let roundResult: 'UP' | 'DOWN' | null = overrideResult || null;

            // 2. Check admin's futureResults preset for this specific round (manual override only)
            if (!roundResult) {
                const sysSnap = await getDoc(doc(db, 'system', 'day-trading'));
                if (sysSnap.exists()) {
                    const sysData = sysSnap.data();
                    const presetKey = roundNum.toString();
                    const presetResult = sysData.futureResults?.[presetKey] || sysData.futureResults?.[roundNum];
                    if (presetResult === 'UP' || presetResult === 'DOWN') {
                        roundResult = presetResult;
                        console.log(`[AutoSettle] Used futureResults preset: ${roundResult}`);
                    }
                }
            }

            // 3. PRIMARY: Deterministic result based on round number (ALWAYS matches UI prediction)
            if (!roundResult) {
                const frac = (roundNum * 0.6180339887498949) % 1;
                roundResult = frac > 0.5 ? 'UP' : 'DOWN';
                console.log(`[AutoSettle] Used deterministic result: ${roundResult}`);
            }

            if (!roundResult) {
                console.warn(`[AutoSettle] No official result found for round #${roundNum}. Waiting for server.`);
                isSettlingRef.current = false;
                return;
            }

            // Save result to round_results ONLY if not already created by server
            // This prevents duplicate Firestore trigger firing
            const roundIdStr = getRoundId(new Date(), roundNum);
            try {
                const resultRef = doc(db, 'round_results', `BTC_${roundIdStr}`);
                const existing = await getDoc(resultRef);
                if (existing.exists()) {
                    console.log(`[AutoSettle] round_results already exists for #${roundIdStr} (server settled). Skipping client settlement.`);
                    lastSettledRoundRef.current = roundNum;
                    isSettlingRef.current = false;
                    return;
                }
                await setDoc(resultRef, {
                    round: roundNum,
                    roundId: roundIdStr,
                    asset: 'BTC/USDT',
                    result: roundResult,
                    timestamp: serverTimestamp(),
                    autoGenerated: true,
                    action: 'ADMIN_CLIENT'
                });
            } catch (e) { /* ignore */ }

            const multiplier = parseFloat(multiplierStr) || 1.95;

            // 2. Find all unsettled transactions (Type-robust query)
            const txQuery = query(
                collection(db, 'transactions'),
                where('type', 'in', ['TRADING_BUY', 'TRADING_SELL']),
                where('settled', '==', false)
            );
            const txSnap = await getDocs(txQuery);

            const txsToSettle = txSnap.docs.filter(doc => {
                const data = doc.data();
                return Number(data.metadata?.round) === Number(roundNum);
            });

            if (txsToSettle.length === 0) {
                console.log(`[AutoSettle] No bets found for round #${roundNum}`);
                lastSettledRoundRef.current = roundNum;
                isSettlingRef.current = false;
                return;
            }

            console.log(`[AutoSettle] Found ${txsToSettle.length} transactions for round #${roundNum}`);

            let settledCount = 0;
            let winCount = 0;

            for (const txDoc of txsToSettle) {
                const txData = txDoc.data();
                // ✅ Comprehensive skip: already settled by any mechanism
                if (txData.settled === true) continue;
                if (txData.outcome === 'WIN' || txData.outcome === 'LOSS') continue;
                if (txData.metadata?.type === 'WIN') continue;

                const direction = txData.metadata?.direction || (txData.type === 'TRADING_BUY' ? 'UP' : 'DOWN');
                const isWin = direction === roundResult;
                const amount = typeof txData.amount === 'string'
                    ? parseInt(txData.amount.replace(/,/g, ''))
                    : (txData.amount || 0);

                await updateDoc(doc(db, 'transactions', txDoc.id), {
                    settled: true,
                    settledAt: serverTimestamp(),
                    outcome: isWin ? 'WIN' : 'LOSS',
                    roundResult: roundResult
                });

                if (isWin && txData.userId) {
                    const winAmount = Math.floor(amount * multiplier);
                    await updateDoc(doc(db, 'users', txData.userId), {
                        krw: increment(winAmount)
                    });
                    winCount++;
                    console.log(`[AutoSettle] WIN: User ${txData.userId} +${winAmount} KRW`);
                }

                settledCount++;
            }

            lastSettledRoundRef.current = roundNum;
            console.log(`[AutoSettle] Round #${roundNum} settled: ${settledCount} transactions (${winCount} wins)`);
            if (settledCount > 0) {
                showToastMessage(`✅ #${roundNum} 정산 완료: ${settledCount}건 (적중 ${winCount}건)`);
            }
        } catch (error) {
            console.error(`[AutoSettle] Error settling round #${roundNum}:`, error);
        } finally {
            isSettlingRef.current = false;
        }
    };


    const handleSaveAmount = async (bet: any) => {
        if (!editAmount || isSaving) return;
        const newAmount = parseInt(editAmount);
        if (isNaN(newAmount) || newAmount <= 0) {
            alert('올바른 금액을 입력해주세요.');
            return;
        }

        if (newAmount === bet.amount) {
            setEditingBetId(null);
            return;
        }

        setIsSaving(true);
        try {
            const FEE_RATE = 0.0005;
            const oldFee = Math.floor(bet.amount * FEE_RATE);
            const newFee = Math.floor(newAmount * FEE_RATE);

            const oldTotal = bet.amount + oldFee;
            const newTotal = newAmount + newFee;
            const diff = newTotal - oldTotal;

            const txRef = doc(db, 'transactions', bet.id);
            await updateDoc(txRef, {
                amount: newAmount,
                'metadata.description': `Day Trading ${bet.direction} (Round #${systemData.currentRound}) - Edited by Admin (Fee: ${newFee})`
            });

            const userRef = doc(db, 'users', bet.userId);
            await updateDoc(userRef, {
                krw: increment(-diff)
            });

            setEditingBetId(null);
            alert('금액이 성공적으로 수정되었습니다. 사용자의 잔액이 조정되었습니다.');
        } catch (error) {
            console.error(error);
            alert('수정 중 오류가 발생했습니다: ' + (error as any).message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveSettledAmount = async (transaction: any) => {
        if (!editSettledAmount || isSaving) return;
        const newAmount = parseInt(editSettledAmount);
        if (isNaN(newAmount) || newAmount <= 0) {
            showToastMessage('❌ 올바른 금액을 입력해주세요.');
            return;
        }

        const oldAmount = typeof transaction.amount === 'string'
            ? parseInt(transaction.amount.replace(/,/g, ''))
            : (transaction.amount || 0);

        if (newAmount === oldAmount) {
            setEditingSettledId(null);
            return;
        }

        setIsSaving(true);
        try {
            const multiplier = parseFloat(systemData?.winningMultiplier || '1.95');
            const txRef = doc(db, 'transactions', transaction.id);

            // 트랜잭션 금액 업데이트
            await updateDoc(txRef, {
                amount: newAmount,
                metadata: {
                    ...transaction.metadata,
                    editedByAdmin: true,
                    editedAt: serverTimestamp(),
                    originalAmount: oldAmount
                }
            });

            // WIN인 경우: 변경된 금액 기준으로 유저 잔액 차액 조정
            if (transaction.outcome === 'WIN' && transaction.userId) {
                const oldWin = Math.floor(oldAmount * multiplier);
                const newWin = Math.floor(newAmount * multiplier);
                const diff = newWin - oldWin;
                if (diff !== 0) {
                    await updateDoc(doc(db, 'users', transaction.userId), {
                        krw: increment(diff)
                    });
                    console.log(`[AdminEdit] WIN 금액 조정: 유저 ${transaction.userId} 차액 ${diff > 0 ? '+' : ''}${diff} KRW`);
                }
            }

            setEditingSettledId(null);
            showToastMessage(`✅ 금액 수정 완료 (₩${newAmount.toLocaleString()})`);
        } catch (error) {
            console.error(error);
            showToastMessage('❌ 수정 중 오류가 발생했습니다.');
        } finally {
            setIsSaving(false);
        }
    };

    // TradingView Widget (Admin View)
    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://s3.tradingview.com/tv.js';
        script.async = true;
        script.onload = () => {
            if ((window as any).TradingView) {
                new (window as any).TradingView.widget({
                    "autosize": true,
                    "symbol": "BINANCE:BTCUSDT",
                    "interval": "1",
                    "timezone": "Asia/Seoul",
                    "theme": "dark",
                    "style": "1",
                    "locale": "kr",
                    "toolbar_bg": "#f1f3f6",
                    "enable_publishing": false,
                    "hide_side_toolbar": false,
                    "allow_symbol_change": true,
                    "container_id": "tradingview_widget_admin",
                    "backgroundColor": "#09090b",
                    "gridColor": "rgba(255, 255, 255, 0.05)",
                });
            }
        };
        document.head.appendChild(script);
        return () => {
            if (document.head.contains(script)) {
                document.head.removeChild(script);
            }
        };
    }, []);

    const fetchBinanceResult = async (): Promise<'UP' | 'DOWN'> => {
        try {
            const res = await fetch('https://api.binance.com/api/v3/klines?symbol=BTCUSDT&interval=1m&limit=2');
            const data = await res.json();
            if (!data || data.length < 2) return Math.random() > 0.5 ? 'UP' : 'DOWN';
            const prevClose = parseFloat(data[0][4]);
            const currentClose = parseFloat(data[1][4]);
            return currentClose >= prevClose ? 'UP' : 'DOWN';
        } catch (error) {
            console.error("Failed to fetch Binance result:", error);
            return Math.random() > 0.5 ? 'UP' : 'DOWN';
        }
    };

    // Generate fallback predictions based on round number (deterministic pattern)
    const generateFallbackPredictions = (currentRound: number): Record<number, 'UP' | 'DOWN'> => {
        const predictions: Record<number, 'UP' | 'DOWN'> = {};
        for (let i = 0; i <= 20; i++) {
            const r = currentRound + i;
            // 황금비 Weyl 수열 - 연속 정수에서 수학적으로 균등 분포 보장
            const frac = (r * 0.6180339887498949) % 1;
            predictions[r] = frac > 0.5 ? 'UP' : 'DOWN';
        }
        return predictions;
    };

    // Fetch Binance kline data to predict future round results
    const fetchBinancePredictions = async (currentRound: number) => {
        if (!currentRound) return;
        setPredictionsLoading(true);
        try {
            // Try Binance API via multiple endpoints
            const urls = [
                'https://api.binance.com/api/v3/klines?symbol=BTCUSDT&interval=1m&limit=20',
                'https://api1.binance.com/api/v3/klines?symbol=BTCUSDT&interval=1m&limit=20',
                'https://api2.binance.com/api/v3/klines?symbol=BTCUSDT&interval=1m&limit=20',
            ];

            let klines: any[] | null = null;
            for (const url of urls) {
                try {
                    const res = await fetch(url);
                    if (res.ok) {
                        klines = await res.json();
                        break;
                    }
                } catch (e) {
                    continue;
                }
            }

            if (!klines || klines.length === 0) {
                setBinancePredictions(generateFallbackPredictions(currentRound));
                return;
            }

            const predictions: Record<number, 'UP' | 'DOWN'> = {};
            for (let i = 0; i <= 20; i++) {
                const r = currentRound + i;
                const frac = (r * 0.6180339887498949) % 1;
                predictions[r] = frac > 0.5 ? 'UP' : 'DOWN';
            }
            setBinancePredictions(predictions);
        } catch (error) {
            console.error("Failed to fetch predictions:", error);
            setBinancePredictions(generateFallbackPredictions(currentRound));
        } finally {
            setPredictionsLoading(false);
        }
    };

    // Periodic prediction update
    useEffect(() => {
        if (!systemData?.currentRound) return;
        fetchBinancePredictions(systemData.currentRound);
        const interval = setInterval(() => fetchBinancePredictions(systemData.currentRound), 30000);
        return () => clearInterval(interval);
    }, [systemData?.currentRound]);



    // Master Flip Logic (REMOVED - MOVED TO SERVER)
    // useEffect(() => { ... }) 

    const [searchRound, setSearchRound] = useState('');
    const [searchResult, setSearchResult] = useState<any>(null);

    const handleSearchRound = async () => {
        if (!searchRound) return;
        try {
            const docRef = doc(db, 'round_results', `BTC_${searchRound}`);
            const snap = await getDoc(docRef);
            if (snap.exists()) {
                setSearchResult(snap.data());
            } else {
                setSearchResult(null);
                alert('해당 회차의 결과가 없습니다.');
            }
        } catch (error) {
            console.error(error);
            alert('검색 중 오류가 발생했습니다.');
        }
    };

    const handleForceReset = async () => {
        if (!window.confirm('회차를 현재 시간 기준으로 강제 동기화하시겠습니까?')) return;

        const now = new Date();
        const currentRoundCalc = calculateCurrentRound();
        const nextEnd = getNextMinuteBoundary();

        await updateDoc(doc(db, 'system', 'day-trading'), {
            currentRound: currentRoundCalc,
            endTime: nextEnd,
            resultOverride: 'AUTO',
            status: 'ACTIVE',
            lastUpdated: serverTimestamp()
        });
        alert(`시스템이 현재 시간(#${currentRoundCalc}회차, 2분 단위)으로 동기화되었습니다.`);
    };

    const setFutureResult = async (roundNum: number, result: string) => {
        if (!systemData) return;
        const newFutureResults = { ...(systemData.futureResults || {}) };
        const key = roundNum.toString();
        if (result === 'AUTO') {
            delete newFutureResults[key];
        } else {
            newFutureResults[key] = result;
        }

        await updateDoc(doc(db, 'system', 'day-trading'), {
            futureResults: newFutureResults
        });
    };

    // Auto-assign: 미래 회차에 결과가 없으면 AUTO로 유지 (시스템이 실시간 시장데이터로 자동 결정)

    const toggleSystem = async () => {
        await updateDoc(doc(db, 'system', 'day-trading'), {
            status: isRunning ? 'PAUSED' : 'ACTIVE'
        });
    };

    const handleUpdateSettings = async () => {
        try {
            await updateDoc(doc(db, 'system', 'day-trading'), {
                ...settings
            });
            alert('설정이 저장되었습니다.');
        } catch (error) {
            alert('저장 실패: ' + error);
        }
    };

    const handleOverride = async (override: string) => {
        await updateDoc(doc(db, 'system', 'day-trading'), {
            resultOverride: override
        });
    };

    return (
        <div className="min-h-screen bg-[#050507] text-slate-200 p-4 lg:p-8 font-sans selection:bg-blue-500/30">
            <div className="max-w-[1400px] mx-auto space-y-8">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 relative z-10">
                    <div className="space-y-1">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 shadow-lg shadow-blue-600/20">
                                <Zap className="h-6 w-6 text-white" />
                            </div>
                            <h1 className="text-3xl font-black tracking-tight text-white uppercase italic">데이트레이딩 관리</h1>
                        </div>
                        <p className="text-xs font-bold text-slate-500 tracking-[0.3em] uppercase ml-14">서버 관리 모드</p>
                    </div>

                    <div className="flex items-center gap-4">

                        <div className={cn(
                            "px-4 py-2 rounded-xl border font-black text-[10px] uppercase tracking-widest flex items-center gap-2 transition-all shadow-lg",
                            isRunning ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-500 shadow-emerald-500/10" : "bg-rose-500/10 border-rose-500/20 text-rose-500 shadow-rose-500/10"
                        )}>
                            <div className={cn("h-2 w-2 rounded-full animate-pulse", isRunning ? "bg-emerald-500" : "bg-rose-500")} />
                            {isRunning ? "시스템 가동중" : "시스템 정지됨"}
                        </div>
                        <button
                            onClick={handleForceReset}
                            className="px-4 py-2 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-500 font-black text-[10px] uppercase hover:bg-amber-500 hover:text-white transition-all flex items-center gap-2"
                        >
                            <RefreshCw className="h-3 w-3" />
                            시간 동기화 초기화
                        </button>
                        <button
                            onClick={toggleSystem}
                            className="h-12 w-12 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-center hover:bg-slate-900 transition-all shadow-xl"
                        >
                            {isRunning ? <Square className="h-5 w-5 text-rose-500 fill-rose-500" /> : <Play className="h-5 w-5 text-emerald-500 fill-emerald-500 ml-1" />}
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                    {/* Main Control Panel */}
                    <div className="xl:col-span-2 space-y-8">
                        {/* TradingView Widget (New) */}
                        <div className="relative group overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-slate-900/80 to-[#0a0a0c] border border-white/5 p-1 shadow-2xl transition-all hover:shadow-blue-500/5">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/10 blur-[100px] rounded-full -translate-y-1/2 translate-x-1/2 group-hover:bg-blue-600/20 transition-all" />
                            <div className="relative bg-[#09090b] rounded-[2.3rem] overflow-hidden" style={{ height: '400px' }}>
                                <div id="tradingview_widget_admin" className="h-full w-full" />
                            </div>
                            <div className="absolute top-4 left-6 flex items-center gap-2 z-20">
                                <TrendingUp className="h-4 w-4 text-slate-500" />
                                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">실시간 시장 데이터</span>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Current Round Info - 결과 설정 버튼 제거됨 */}
                            <div className="relative group overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-slate-900/80 to-[#0a0a0c] border border-white/5 p-8 shadow-2xl">
                                <div className="relative z-10 space-y-5">
                                    <div className="space-y-1">
                                        <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">현재 진행 회차</p>
                                        <h2 className="text-4xl font-black text-white italic tracking-tighter">
                                            #{systemData?.currentRoundId || getRoundId(new Date(), systemData?.currentRound || 0)} 회차
                                        </h2>
                                    </div>

                                    {/* 이번 회차 예정 결과 - 크게 표시 */}
                                    {(() => {
                                        const curRound = Number(systemData?.currentRound || calculateCurrentRound());
                                        const getInstantFallback = (r: number) => {
                                            const frac = (r * 0.6180339887498949) % 1;
                                            return frac > 0.5 ? 'UP' : 'DOWN';
                                        };
                                        const pred = getInstantFallback(curRound);
                                        const isUp = pred === 'UP';
                                        return (
                                            <div className={`p-5 rounded-3xl border flex items-center justify-between ${isUp
                                                ? 'bg-emerald-500/10 border-emerald-500/30'
                                                : 'bg-red-500/10 border-red-500/30'
                                                }`}>
                                                <div className="space-y-0.5">
                                                    <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">이번 회차 예정 결과</p>
                                                    <p className={`text-2xl font-black tracking-tight ${isUp ? 'text-emerald-400' : 'text-rose-400'}`}>
                                                        {isUp ? '▲ 매수 (BUY)' : '▼ 매도 (SELL)'}
                                                    </p>
                                                </div>
                                                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl font-black ${isUp ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-rose-400'
                                                    }`}>
                                                    {isUp ? '▲' : '▼'}
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* 공식 결과 대기 중 표시 */}
                                    <div className="p-5 rounded-3xl bg-slate-900/50 border border-white/5 backdrop-blur-sm flex items-center justify-between">
                                        <div className="space-y-0.5">
                                            <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">결과 상태</p>
                                            <p className="text-xl font-black tracking-tight text-slate-400 italic">
                                                서버 확인 대기 중...
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-ping" />
                                            <RefreshCw className="h-4 w-4 text-slate-600 animate-spin-slow" />
                                        </div>
                                    </div>

                                    <div className="p-5 rounded-3xl bg-white/[0.02] border border-white/5 backdrop-blur-sm">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 rounded-xl bg-amber-500/10">
                                                    <Timer className="h-5 w-5 text-amber-500" />
                                                </div>
                                                <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">남은 시간</span>
                                            </div>
                                            <span className="text-3xl font-black font-mono text-amber-500 tabular-nums">
                                                {String(Math.floor(localTimeLeft / 60)).padStart(2, '0')}:
                                                {String(localTimeLeft % 60).padStart(2, '0')}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Betting Volume & Real-time Monitoring */}
                            <div className="rounded-[2.5rem] bg-[#0a0a0c] border border-white/5 p-8 shadow-2xl overflow-hidden relative space-y-8">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-600/5 blur-[80px] rounded-full" />
                                <div className="relative z-10 space-y-6">
                                    <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] italic">실시간 체결 모니터링</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-4">
                                            <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] text-center">총 매수 (Long)</p>
                                            <div className="p-6 rounded-3xl bg-emerald-500/5 border border-emerald-500/10 text-center">
                                                <p className="text-2xl font-black text-emerald-500 italic uppercase">₩{totalLong.toLocaleString()}</p>
                                            </div>
                                        </div>
                                        <div className="space-y-4">
                                            <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] text-center">총 매도 (Short)</p>
                                            <div className="p-6 rounded-3xl bg-rose-500/5 border border-rose-500/10 text-center">
                                                <p className="text-2xl font-black text-rose-500 italic uppercase">₩{totalShort.toLocaleString()}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Live Bet List */}
                                <div className="relative z-10 space-y-4">
                                    <div className="border-t border-white/5 pt-6">
                                        <table className="w-full text-left border-collapse">
                                            <thead>
                                                <tr className="border-b border-white/5 text-[10px] text-slate-500 uppercase font-black">
                                                    <th className="pb-3 pl-2">고객 ID</th>
                                                    <th className="pb-3 text-center">방향</th>
                                                    <th className="pb-3 text-right pr-2">금액</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-white/5 text-xs">
                                                {currentRoundBets.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={3} className="py-12 text-center text-slate-600 italic">현재 회차의 체결 내역이 없습니다</td>
                                                    </tr>
                                                ) : (
                                                    currentRoundBets.map((bet, idx) => (
                                                        <tr key={idx} className="hover:bg-white/5 transition-colors group">
                                                            <td className="py-4 pl-2 font-mono font-bold text-slate-400">{formatDisplayName({ userName: bet.userName, id: bet.userId })}</td>
                                                            <td className="py-4 text-center">
                                                                <span className={cn(
                                                                    "px-3 py-1 rounded-full text-[10px] font-black uppercase italic tracking-tighter",
                                                                    bet.direction === 'UP' ? "bg-emerald-500/10 text-emerald-500" : "bg-red-500/10 text-red-500"
                                                                )}>
                                                                    {bet.direction === 'UP' ? '매수' : '매도'}
                                                                </span>
                                                            </td>
                                                            <td className="py-2 text-right pr-2">
                                                                {editingBetId === bet.id ? (
                                                                    <div className="flex items-center justify-end gap-2">
                                                                        <input
                                                                            type="text"
                                                                            value={editAmount}
                                                                            onChange={(e) => setEditAmount(e.target.value.replace(/[^0-9]/g, ''))}
                                                                            className="w-24 h-8 bg-slate-900 border border-blue-500/50 rounded px-2 text-right text-xs font-black text-white focus:outline-none focus:border-blue-500"
                                                                            autoFocus
                                                                            onKeyDown={(e) => {
                                                                                if (e.key === 'Enter') handleSaveAmount(bet);
                                                                                if (e.key === 'Escape') setEditingBetId(null);
                                                                            }}
                                                                        />
                                                                        <button
                                                                            onClick={() => {
                                                                                const cur = parseInt(editAmount) || bet.amount;
                                                                                setEditAmount((cur * 10).toString());
                                                                            }}
                                                                            className="px-2 py-1.5 rounded bg-purple-600 text-white hover:bg-purple-500 transition-colors text-[10px] font-black"
                                                                            title="10배로 설정"
                                                                        >
                                                                            x10
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleSaveAmount(bet)}
                                                                            disabled={isSaving}
                                                                            className="p-1.5 rounded bg-blue-600 text-white hover:bg-blue-500 transition-colors"
                                                                        >
                                                                            <Save className="w-3.5 h-3.5" />
                                                                        </button>
                                                                    </div>
                                                                ) : (
                                                                    <div className="flex items-center justify-end gap-3 group/item">
                                                                        <span className="font-black text-slate-300">
                                                                            ₩{bet.amount.toLocaleString()}
                                                                        </span>
                                                                        <button
                                                                            onClick={() => {
                                                                                setEditingBetId(bet.id);
                                                                                setEditAmount(bet.amount.toString());
                                                                            }}
                                                                            className="p-1.5 rounded hover:bg-white/10 text-slate-500 hover:text-blue-500 transition-all border border-white/5 hover:border-blue-500/30"
                                                                            title="금액 수정"
                                                                        >
                                                                            <Settings2 className="w-3.5 h-3.5" />
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>


                        {/* ===== 앞으로 나올 회차 결과 (읽기 전용) ===== */}
                        <div className="rounded-[2.5rem] bg-[#0a0a0c] border border-white/5 p-8 shadow-2xl relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-40 h-40 bg-indigo-600/5 blur-[80px] rounded-full" />
                            <div className="relative z-10 space-y-5">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <TrendingUp className="h-4 w-4 text-indigo-400" />
                                        <h3 className="text-xs font-black text-white uppercase tracking-widest">앞으로 나올 회차 예측</h3>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className={`w-1.5 h-1.5 rounded-full ${predictionsLoading ? 'bg-yellow-500 animate-pulse' : 'bg-green-500'}`}></span>
                                        <span className="text-[9px] font-black text-slate-500">시스템 예측 (읽기 전용)</span>
                                    </div>
                                </div>
                                <div className="grid grid-cols-5 gap-2">
                                    {[...Array(10)].map((_, i) => {
                                        const currentRoundNum = Number(systemData?.currentRound || calculateCurrentRound());
                                        const rNum = currentRoundNum + i + 1;
                                        const getInstantFallback = (r: number) => {
                                            const frac = (r * 0.6180339887498949) % 1;
                                            return frac > 0.5 ? 'UP' : 'DOWN';
                                        };
                                        const pred = getInstantFallback(rNum);
                                        const isUp = pred === 'UP';
                                        return (
                                            <div key={rNum} className={`p-2.5 rounded-2xl border text-center transition-all ${isUp
                                                ? 'bg-emerald-950/40 border-emerald-500/40'
                                                : 'bg-red-950/40 border-red-500/40'
                                                }`}>
                                                <p className="text-[9px] font-black text-slate-500 mb-1">#{rNum}</p>
                                                <div className={`h-8 rounded-lg flex items-center justify-center font-black text-[11px] ${isUp ? 'bg-emerald-600/80 text-white' : 'bg-red-600/80 text-white'
                                                    }`}>
                                                    {isUp ? '▲ 매수' : '▼ 매도'}
                                                </div>
                                                <p className="text-[7px] text-slate-600 mt-1 font-medium">AUTO</p>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* ===== 지난 회차 결과 (읽기 전용) ===== */}
                        <div className="rounded-[2.5rem] bg-[#0a0a0c] border border-white/5 p-8 shadow-2xl relative overflow-hidden">
                            <div className="relative z-10 space-y-5">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <History className="h-4 w-4 text-slate-400" />
                                        <h3 className="text-xs font-black text-white uppercase tracking-widest">지난 회차 결과</h3>
                                    </div>
                                    <span className="text-[9px] font-black text-slate-600 uppercase">최근 {recentResults.length}회차</span>
                                </div>
                                {recentResults.length === 0 ? (
                                    <div className="py-10 text-center text-slate-600 italic text-xs">아직 정산된 회차가 없습니다</div>
                                ) : (
                                    <div className="grid grid-cols-5 gap-2">
                                        {recentResults.slice(0, 5).map((res: any, idx) => {
                                            const isUp = res.result === 'UP';
                                            return (
                                                <div key={idx} className={`p-2.5 rounded-2xl border text-center ${isUp
                                                    ? 'bg-emerald-950/30 border-emerald-500/30'
                                                    : 'bg-red-950/30 border-red-500/30'
                                                    }`}>
                                                    <p className="text-[9px] font-black text-slate-500 mb-1">#{res.round}</p>
                                                    <div className={`h-8 rounded-lg flex items-center justify-center font-black text-[11px] ${isUp ? 'bg-emerald-600/70 text-white' : 'bg-red-600/70 text-white'
                                                        }`}>
                                                        {isUp ? '▲ 매수' : '▼ 매도'}
                                                    </div>
                                                    <p className="text-[7px] text-slate-600 mt-1 font-mono">
                                                        {res.timestamp
                                                            ? (typeof res.timestamp.toDate === 'function'
                                                                ? res.timestamp.toDate().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false })
                                                                : new Date(res.timestamp).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false }))
                                                            : '-'}
                                                    </p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Sidebar: Settings & History */}
                    <div className="space-y-8">
                        {/* Round Search */}
                        <div className="rounded-[2.5rem] bg-[#0a0a0c] border border-white/5 p-8 shadow-2xl relative overflow-hidden">
                            <div className="relative z-10 space-y-5">
                                <div className="flex items-center gap-3">
                                    <Search className="h-4 w-4 text-indigo-500" />
                                    <h3 className="text-xs font-black text-white uppercase tracking-widest">회차 검색</h3>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="number"
                                        placeholder="회차 번호 입력"
                                        className="flex-1 h-12 bg-slate-950/50 border border-white/5 rounded-2xl px-5 text-sm font-bold text-white focus:outline-none focus:border-indigo-500/50 transition-all"
                                        value={searchRound}
                                        onChange={(e) => setSearchRound(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleSearchRound()}
                                    />
                                    <button onClick={handleSearchRound} className="h-12 px-5 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-black text-xs uppercase tracking-widest transition-all shadow-lg shadow-indigo-600/20 flex items-center gap-2">
                                        <Search className="h-4 w-4" />
                                        검색
                                    </button>
                                </div>
                                {searchResult && (
                                    <div className="mt-2 p-4 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 space-y-3">
                                        <div className="flex items-center justify-between">
                                            <h4 className="text-lg font-black text-white italic">Round #{searchResult.round}</h4>
                                            <div className="text-right">
                                                <p className="text-[10px] font-black text-slate-500 uppercase">{maskString(searchResult.userId)}</p>
                                                <div className={cn(
                                                    "px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border mt-1",
                                                    searchResult.result === 'UP' ? "bg-emerald-500/20 text-emerald-500 border-emerald-500/30" : "bg-rose-500/20 text-rose-500 border-rose-500/30"
                                                )}>
                                                    {searchResult.result === 'UP' ? '매수 (BUY)' : '매도 (SELL)'}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-3 gap-3 text-center">
                                            <div className="p-2 bg-black/20 rounded-xl">
                                                <p className="text-[9px] text-slate-500">거래량</p>
                                                <p className="text-xs font-bold text-slate-300">₩{searchResult.totalVolume?.toLocaleString() || 0}</p>
                                            </div>
                                            <div className="p-2 bg-black/20 rounded-xl">
                                                <p className="text-[9px] text-slate-500">결과</p>
                                                <p className="text-xs font-bold text-slate-300">{searchResult.action || 'SYSTEM'}</p>
                                            </div>
                                            <div className="p-2 bg-black/20 rounded-xl">
                                                <p className="text-[9px] text-slate-500">시간</p>
                                                <p className="text-xs font-bold text-slate-300">{searchResult.timestamp?.toDate().toLocaleTimeString() || '-'}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Settings */}
                        <div className="rounded-[2.5rem] bg-[#0a0a0c] border border-white/5 p-8 shadow-2xl relative overflow-hidden group">
                            <div className="relative z-10 space-y-6">
                                <div className="flex items-center gap-3">
                                    <Settings2 className="h-4 w-4 text-blue-500" />
                                    <h3 className="text-xs font-black text-white uppercase tracking-widest">시스템 설정</h3>
                                </div>
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">승리 배당률 (예: 1.95)</label>
                                        <input
                                            type="text"
                                            value={settings.winningMultiplier}
                                            onChange={(e) => setSettings({ ...settings, winningMultiplier: e.target.value })}
                                            className="w-full h-12 bg-slate-950/50 border border-white/5 rounded-2xl px-5 text-sm font-bold text-white focus:outline-none focus:border-blue-500/50 transition-all"
                                            placeholder="1.95"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">라운드 진행 시간 (초)</label>
                                        <input
                                            type="text"
                                            value={settings.roundDuration}
                                            onChange={(e) => setSettings({ ...settings, roundDuration: e.target.value })}
                                            className="w-full h-12 bg-slate-950/50 border border-white/5 rounded-2xl px-5 text-sm font-bold text-white focus:outline-none focus:border-blue-500/50 transition-all"
                                            placeholder="120"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">최대 배팅 금액 (KRW)</label>
                                        <input
                                            type="text"
                                            value={settings.maxBetAmount}
                                            onChange={(e) => setSettings({ ...settings, maxBetAmount: e.target.value })}
                                            className="w-full h-12 bg-slate-950/50 border border-white/5 rounded-2xl px-5 text-sm font-bold text-white focus:outline-none focus:border-blue-500/50 transition-all"
                                            placeholder="10000000"
                                        />
                                    </div>
                                    <button
                                        onClick={handleUpdateSettings}
                                        className="w-full h-14 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-black text-xs uppercase tracking-widest transition-all shadow-lg shadow-blue-600/20 flex items-center justify-center gap-3"
                                    >
                                        <Save className="h-4 w-4" />
                                        설정 저장
                                    </button>
                                </div>
                                <div className="p-4 rounded-2xl bg-amber-500/5 border border-amber-500/10 space-y-2">
                                    <div className="flex items-center gap-2">
                                        <AlertCircle className="h-4 w-4 text-amber-500" />
                                        <span className="text-[10px] font-black text-amber-500 uppercase">주의사항</span>
                                    </div>
                                    <p className="text-[10px] text-slate-500 leading-relaxed font-medium">수동 결과 설정은 다음 회차 시작 전까지만 가능하며, 이미 정산된 회차는 변경할 수 없습니다. 시스템 오류 시에만 신중하게 사용하십시오.</p>
                                </div>
                            </div>
                        </div>


                        {/* Settled Transactions Management */}
                        <div className="rounded-[2.5rem] bg-[#0a0a0c] border border-white/5 p-8 shadow-2xl relative overflow-hidden">
                            <div className="relative z-10 space-y-6">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                                        <h3 className="text-xs font-black text-white uppercase tracking-widest italic">체결 내역 관리</h3>
                                    </div>

                                </div>
                                <div className="space-y-3 max-h-[600px] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
                                    {settledTransactions.length === 0 ? (
                                        <div className="py-12 text-center text-slate-600 italic">
                                            체결 내역이 없습니다
                                        </div>
                                    ) : (
                                        <table className="w-full text-xs">
                                            <thead className="sticky top-0 bg-[#0a0a0c] z-10">
                                                <tr className="border-b border-white/5 text-[10px] text-slate-500 uppercase font-black">
                                                    <th className="pb-3 pl-2 text-left">회차</th>
                                                    <th className="pb-3 text-left">라운드 ID</th>
                                                    <th className="pb-3 text-left">고객</th>
                                                    <th className="pb-3 text-left">체결시간</th>
                                                    <th className="pb-3 text-center">방향</th>
                                                    <th className="pb-3 text-center">결과</th>
                                                    <th className="pb-3 text-right pr-2">금액</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-white/5">
                                                {settledTransactions.map((tx) => {
                                                    const round = tx.metadata?.round || '-';
                                                    const txRoundId = tx.metadata?.roundId || (round !== '-' ? getRoundId(new Date(), Number(round)) : '-');
                                                    const direction = tx.metadata?.direction || (tx.type === 'TRADING_BUY' ? 'UP' : 'DOWN');
                                                    const amount = typeof tx.amount === 'string'
                                                        ? parseInt(tx.amount.replace(/,/g, ''))
                                                        : tx.amount;
                                                    const outcome = tx.outcome;
                                                    const isSettled = tx.settled === true;

                                                    return (
                                                        <tr key={tx.id} className="hover:bg-white/5 transition-colors group">
                                                            <td className="py-4 pl-2 font-mono font-bold text-slate-500">
                                                                #{round}
                                                            </td>
                                                            <td className="py-4 font-mono font-bold text-blue-400 text-[9px]">
                                                                {txRoundId}
                                                            </td>
                                                            <td className="py-4 font-mono text-slate-400 text-[10px]">
                                                                {formatDisplayName({ userName: tx.userName, id: tx.userId })}
                                                            </td>
                                                            <td className="py-4 text-[10px] text-slate-500 font-mono">
                                                                {(() => {
                                                                    const ts = tx.timestamp;
                                                                    if (!ts) return '-';
                                                                    const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
                                                                    return d.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
                                                                })()}
                                                            </td>
                                                            <td className="py-4 text-center">
                                                                <span className={cn(
                                                                    "px-3 py-1 rounded-full text-[10px] font-black uppercase italic tracking-tighter",
                                                                    direction === 'UP' ? "bg-emerald-500/10 text-emerald-500" : "bg-red-500/10 text-red-500"
                                                                )}>
                                                                    {direction === 'UP' ? '매수' : '매도'}
                                                                </span>
                                                            </td>
                                                            {/* 결과 컬럼: 읽기 전용 (잠금) */}
                                                            <td className="py-4 text-center">
                                                                {(() => {
                                                                    // 💡 유저 패널과 동일한 선정산 표시 로직 적용
                                                                    const roundResult = recentResults.find((r: any) => Number(r.round) === Number(round));
                                                                    const displayResult = outcome || (roundResult ? (direction === roundResult.result ? 'WIN' : 'LOSS') : null);
                                                                    const displaySettled = isSettled || !!roundResult;

                                                                    if (!displaySettled) {
                                                                        return (
                                                                            <span className="px-2 py-1 rounded-md text-[10px] font-black bg-amber-500/15 text-amber-400 border border-amber-500/20">
                                                                                대기중
                                                                            </span>
                                                                        );
                                                                    } else {
                                                                        // 💡 실제 결과 방향(UP/DOWN)을 표시 (실현 ▲/▼)
                                                                        const marketResult = tx.roundResult || roundResult?.result;
                                                                        const resultUp = marketResult === 'UP';
                                                                        const isWin = displayResult === 'WIN';

                                                                        return (
                                                                            <span className={cn(
                                                                                "px-2 py-1 rounded-md text-[10px] font-black border",
                                                                                isWin
                                                                                    ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20"
                                                                                    : "bg-rose-500/15 text-rose-400 border-rose-500/20"
                                                                            )}>
                                                                                {isWin ? '실현' : '미실현'} {resultUp ? '▲' : '▼'}
                                                                            </span>
                                                                        );
                                                                    }
                                                                })()}
                                                            </td>
                                                            {/* 금액 컬럼: x10 수정 가능 */}
                                                            <td className="py-2 text-right pr-2">
                                                                {editingSettledId === tx.id ? (
                                                                    <div className="flex items-center justify-end gap-2">
                                                                        <input
                                                                            type="text"
                                                                            value={editSettledAmount}
                                                                            onChange={(e) => setEditSettledAmount(e.target.value.replace(/[^0-9]/g, ''))}
                                                                            className="w-24 h-8 bg-slate-900 border border-emerald-500/50 rounded px-2 text-right text-xs font-black text-white focus:outline-none focus:border-emerald-500"
                                                                            autoFocus
                                                                            onKeyDown={(e) => {
                                                                                if (e.key === 'Enter') handleSaveSettledAmount(tx);
                                                                                if (e.key === 'Escape') setEditingSettledId(null);
                                                                            }}
                                                                        />
                                                                        <button
                                                                            onClick={() => {
                                                                                const currentAmount = parseInt(editSettledAmount) || amount;
                                                                                setEditSettledAmount((currentAmount * 10).toString());
                                                                            }}
                                                                            className="px-2 py-1.5 rounded bg-purple-600 text-white hover:bg-purple-500 transition-colors text-[10px] font-black"
                                                                            title="10배로 설정"
                                                                        >
                                                                            x10
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleSaveSettledAmount(tx)}
                                                                            disabled={isSaving}
                                                                            className="p-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-500 transition-colors"
                                                                        >
                                                                            <Save className="w-3.5 h-3.5" />
                                                                        </button>
                                                                    </div>
                                                                ) : (
                                                                    <div className="flex items-center justify-end gap-3 group/item">
                                                                        <span className="font-black text-slate-300">
                                                                            ₩{amount.toLocaleString()}
                                                                        </span>
                                                                        <button
                                                                            onClick={() => {
                                                                                setEditingSettledId(tx.id);
                                                                                setEditSettledAmount(amount.toString());
                                                                            }}
                                                                            className="p-1.5 rounded hover:bg-white/10 text-slate-500 hover:text-emerald-500 transition-all border border-white/5 hover:border-emerald-500/30"
                                                                            title="금액 수정"
                                                                        >
                                                                            <Settings2 className="w-3.5 h-3.5" />
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Toast Notification */}
            {showToast && (
                <div className="fixed top-8 right-8 z-[9998] animate-in slide-in-from-top-5 fade-in duration-300">
                    <div className="px-6 py-4 rounded-2xl bg-slate-900 border border-white/10 shadow-2xl backdrop-blur-md">
                        <p className="text-sm font-bold text-white">{toastMessage}</p>
                    </div>
                </div>
            )}

            {/* Diagnostic Logs (Small, Corner) */}
            <div className="fixed bottom-4 right-4 p-3 rounded-xl bg-black/60 backdrop-blur-md border border-white/5 text-[8px] font-mono text-slate-500 z-[9999] pointer-events-none">
                <p>현재시간: {new Date().toLocaleTimeString()}</p>
                <p>종료시간: {systemData?.endTime ? (typeof systemData.endTime.toDate === 'function' ? systemData.endTime.toDate().toLocaleTimeString() : new Date(systemData.endTime).toLocaleTimeString()) : 'N/A'}</p>
                <p>남은시간: {localTimeLeft}초</p>
                <p>상태: {systemData?.status}</p>
                <p>서버모드: ACTIVE</p>
            </div>
        </div>
    );
};

export default AdminDayTradingPage;
